import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
// import NProgress from 'nprogress';

import { showToast } from '@/utilities/toast-handler';
import { authApi } from '@/utilities/axios';
import { useAuthStore } from '../../../../../modules/auth/store';
import { formatTreeData, formatVueTreeSelectData, flattenArray } from '../../../../../utilities/json-formatter';
import EventBus from '../../../../../utilities/event-bus';
import { truncateText } from '../../../../../utilities/index';


const THEME_URL = process.env.VUE_APP_API_VERSION + '/themes';

export const usethemeStore = defineStore('theme', () => {
    const authStore = useAuthStore();
    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId;
    });

    //state
    const theme = ref({
        name: null,
        parent_id: null,
        company_id: null,
        department_id: null,
    });

    const themes = ref([]);
    const loading = ref(false);
    const editId = ref(null);
    const max_chars = process.env.VUE_APP_MAX_ENTITY_CHARACTERS || 30;

    // getters
    const themeTreeTable = computed(() => {
        return formatTreeDatatableCustom(themes.value);
    });

    const themeTree = computed(() => {
        return formatTreeData(themes.value);
    });

    const themeVueTree = computed(() => {
        return formatVueTreeSelectData(themes.value);
    });

    const flatThemeData = computed(() => {
        return flattenArray(themes.value);
    });

    //actions
    const fetchAllThemes = async (onlyVisible = false) => {
        try {
            // NProgress.start();
            loading.value = true;
            const response = await authApi.get(THEME_URL, {
                params: {
                    company_id: company_id.value,
                     only_visible: onlyVisible
                },
            });
            if (response.statusCode === 200) {
                themes.value = response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch themes');
        } finally {
            // NProgress.done();
            loading.value = false;
        }
    };

    const createTheme = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...theme.value, company_id: company_id.value };
            const response = await authApi.post(THEME_URL, bodyParams);
            if (response.statusCode === 201) {
                fetchAllThemes();
                showToast(response.toast, 'New theme added successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to add theme');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const updateTheme = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...theme.value, company_id: company_id.value };
            const response = await authApi.patch(`${THEME_URL}/${editId.value}`, bodyParams);
            if (response.statusCode === 200) {
                fetchAllThemes();
                showToast(response.toast, 'Theme updated successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to update theme');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const deleteTheme = async (themeId) => {
        try {
            EventBus.emit('show-operation-loader');
            const response = await authApi.delete(`${THEME_URL}/${themeId}`);
            if (response.statusCode === 200) {
                showToast(response.toast, 'Theme deleted successfully');
                fetchAllThemes();
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to delete theme');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const fetchThemeDetails = async (themeId) => {
        try {
            const response = await authApi.get(`${THEME_URL}/${themeId}`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                return response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch theme details');
        }
    };

    // helpers
    const initTheme = () => {
        theme.value.name = null;
        theme.value.parent_id = null;
        theme.value.company_id = null;
        theme.value.department_id = null;
        editId.value = null;
    };

    const createEditThemeBody = (editTheme) => {
        editId.value = editTheme?.key;
        theme.value.name = editTheme?.data?.name;
        theme.value.parent_id = editTheme?.data?.parent_id;
        theme.value.department_id = editTheme?.data?.department_id;
    };

    // load theme by id -> Look in the loaded themes,
    const getThemeDetailsById = (themeId) => {
        const themeData = flatThemeData.value.find((theme) => theme.id === themeId);
        return themeData ?? null;
    };

    const formatTreeDatatableCustom = (data) => {
        let resData = [];
        for (let item of data) {
            let nodeData = {
                key: item.id,
                data: {
                    id: item.id,
                    name: truncateText(item.name, max_chars),
                    childCount: item.children.length,
                    parent_id: item.parent_id,
                    department_id: item?.department_id,
                    users_count: item?.users_count,
                    visibility: item.visibility,
                },
                children: item.children.length > 0 ? formatTreeDatatableCustom(item.children) : [],
            };
            resData.push(nodeData);
        }
        return resData;
    };

    const toggleThemeVisibility = async (themeId, value) => {
        const themeObj = findThemeById(themes.value, themeId); // recursive search
        if (!themeObj) return;
      
        const oldValue = themeObj.visibility;
        themeObj.visibility = value; // mutate source
      
        try {
          await authApi.patch(`/v2/themes/${themeId}/visibility`, {
            visibility: value ? 1 : 0,
          });
          showToast({ severity: 'success', summary: 'Success' }, `Theme visibility updated`);
        } catch (err) {
          themeObj.visibility = oldValue; // rollback
          showToast({ severity: 'error', summary: 'Error' }, 'Failed to update visibility');
        }
      };

      const findThemeById = (nodes, id) => {
        for (const node of nodes) {
          if (node.id === id) return node;
          if (node.children?.length) {
            const found = findThemeById(node.children, id);
            if (found) return found;
          }
        }
        return null;
      };


    return {
        //state
        theme,
        themes,
        editId,

        //getters
        themeTreeTable,
        themeTree,
        themeVueTree,
        flatThemeData,

        //action
        fetchAllThemes,
        createTheme,
        updateTheme,
        deleteTheme,
        fetchThemeDetails,

        //helpers
        initTheme,
        createEditThemeBody,
        getThemeDetailsById,

        toggleThemeVisibility
    };
});
